import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { addDays, format } from 'date-fns';
import {
  animationFrames,
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  shareReplay,
  Subject,
  switchMap,
  takeUntil,
} from 'rxjs';
import { debounceTime, takeWhile, tap } from 'rxjs/operators';
import { Jobs } from 'server/shared/collections/jobs';
import {
  getJobsDefaultFilterSaveParams,
  normalizeJobsDefaultFilterForLoad,
  omitNullJobsDefaultFilterValues,
} from 'server/shared/helpers/jobs-query/getEmptyJobsDefaultFilter';
import {
  getJobsMongoQuery,
  getMatchingShiftsForSearchText,
} from 'server/shared/helpers/jobs-query/getJobsMongoQuery';
import { SearchTypes } from 'server/shared/helpers/jobs-query/SearchTypes';
import { Department } from 'server/shared/models/department';
import { Job } from 'server/shared/models/job';
import { JobStatusEnum } from 'server/shared/models/jobStatusEnum';
import { ShiftEnum } from 'server/shared/models/shift.enum';
import { DispoUser } from 'server/shared/models/user';
import { dbDayDateToken } from 'server/static/dbDayDateToken';
import { CustomerService } from 'src/app/services/customer.service';
import { UserService } from 'src/app/services/user.service';
import { getInitialJobIndexFiltersCollapsed } from 'src/helpers/getInitialJobIndexFiltersCollapsed';
import {
  Column,
  ColumnResizeManager,
  defaultDispoJobColumns,
  optionalJobColumns,
} from 'src/helpers/job/jobColumns';
import { queryParam$ } from 'src/helpers/queryParam$';
import { rxDistinctJSONStringify } from 'src/helpers/rxDistinctJSONStringify';
import { rxNgZoneRun } from 'src/helpers/rxNgZoneRun';
import {
  filterIsNotEmpty,
  rxFilterIsNotEmpty,
} from '../../../helpers/filterIsNotEmpty';
import { rxAfterInitial } from '../../../helpers/rxAfterInitial';
import { PermissionsService } from '../../services/permissions.service';
import { ToastService } from '../../services/toast/toast.service';
import { TranslationService } from '../../services/translation.service';
import { SubscriptionService } from '../../shared/subscription.service';

export type DayListItem = {
  date: string;
  previousDateRangeStart?: string;
  previousDateRangeEnd?: string;
  jobIds?: string[];
};
@Component({
  selector: 'app-dispo-jobs-index',
  templateUrl: './dispo-jobs-index.component.html',
  styleUrls: [
    './dispo-jobs-index.component.scss',
    '../../../stylesheets/components/atoms/jobs-timeline.scss',
    '../../../stylesheets/components/atoms/jobs-list-view.scss',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DispoJobsIndexComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  tabParam$: Observable<JobStatusEnum> = this.route.queryParams.pipe(
    map((params) => {
      let paramString: string = params.state?.toUpperCase();
      let paramEnum = JobStatusEnum.OPEN;
      if (Object.values(JobStatusEnum).includes(paramString as JobStatusEnum)) {
        paramEnum = paramString as JobStatusEnum;
      }
      this.activeTab$.next(paramEnum);
      return paramEnum;
    }),
    // distinctUntilChanged is needed in case of other params changing
    distinctUntilChanged(),
    tap((tab) => {
      // maintained subscriptions that are never unsusbcribed to keep data when switching tabs
      tab === JobStatusEnum.ARCHIVED
        ? this.subscriptionService.initPersistentArchivedJobsSubscription()
        : this.subscriptionService.initPersistentJobsSubscription();
    }),
    shareReplay(1),
  );

  $isLoadingJobs = this.tabParam$.pipe(
    switchMap((state) => {
      return state === JobStatusEnum.ARCHIVED
        ? this.subscriptionService.archivedJobsLoading$
        : this.subscriptionService.jobs.loading$;
    }),
    shareReplay(1),
    rxNgZoneRun(this.ngZone),
  );

  queryParams$ = this.route.queryParams.pipe(
    rxDistinctJSONStringify(),
    shareReplay(1),
  );
  user$ = new BehaviorSubject<DispoUser | undefined>(undefined);

  selectedSearchType$ = new BehaviorSubject<SearchTypes>('ALL');
  searchTextParam$ = this.selectedSearchType$.pipe(
    switchMap((selectedSearchType) =>
      queryParam$(this.route, selectedSearchType),
    ),
  );
  showCanceledJobs$ = new BehaviorSubject<boolean>(false);
  onlyShowCanceledJobs$ = new BehaviorSubject<boolean>(false);
  showOnlyActionRequiredBookableJobs$ = new BehaviorSubject<boolean>(false);
  showOnlyActionRequiredNoOpenInviteesJobs$ = new BehaviorSubject<boolean>(
    false,
  );
  onyShowBookedJobs$ = new BehaviorSubject<boolean>(false);
  showUnmappedJobs$ = new BehaviorSubject<boolean>(false);
  showOnlyUnapprovedHours$ = new BehaviorSubject<boolean>(false);

  jobsSort$ = this.tabParam$.pipe(
    map((state) => (state === JobStatusEnum.ARCHIVED ? -1 : 1)),
    distinctUntilChanged(),
  );

  private readonly jobsQueryInputs$ = combineLatest([
    this.queryParams$,
    this.user$.pipe(rxFilterIsNotEmpty()),
    this.showCanceledJobs$,
    this.onlyShowCanceledJobs$,
    this.showOnlyActionRequiredBookableJobs$,
    this.showOnlyActionRequiredNoOpenInviteesJobs$,
    this.onyShowBookedJobs$,
    this.showUnmappedJobs$,
    this.showOnlyUnapprovedHours$,
    this.selectedSearchType$,
    this.searchTextParam$,
  ]);

  jobsQuery$ = this.jobsQueryInputs$.pipe(
    map((inputs) => this.buildJobsMongoQueryFromInputs(inputs)),
    filter(filterIsNotEmpty),
    rxDistinctJSONStringify(),
    shareReplay(1),
    rxAfterInitial(
      // more debounce leads to more reliable smooth tab-switching animation
      debounceTime(300),
    ),
  );

  activeView$ = new BehaviorSubject<'tiles' | 'list'>(
    (localStorage.getItem('jobActiveView') as 'tiles' | 'list') || 'tiles',
  );

  filtersCollapsed$ = new BehaviorSubject<boolean>(
    getInitialJobIndexFiltersCollapsed(),
  );

  filterCounts$ = this.route.queryParams.pipe(
    rxDistinctJSONStringify(),
    filter(filterIsNotEmpty),
    map((params) => {
      let count = 0;
      Object.keys(params).forEach((key) => {
        if (key === 'state') {
          return;
        }
        if (key === 'range' && params[key] === 'ALL') {
          return;
        }
        if (key === 'jobType' && params[key] === 'ALL') {
          return;
        }
        if (!params[key] || params[key] === 'false') {
          return;
        }
        count++;
      });
      return count;
    }),
    shareReplay(1),
  );

  today$ = new BehaviorSubject<string>(format(new Date(), dbDayDateToken));

  destroyed$ = new Subject<void>();

  // isLoadingJobs initializes the subscriptions
  allJobs$ = combineLatest([
    this.jobsQuery$,
    this.jobsSort$,
    this.tabParam$,
    this.$isLoadingJobs,
  ]).pipe(
    switchMap(([query, dateSort, tab, isLoading]) => {
      if (tab === JobStatusEnum.ARCHIVED && isLoading) {
        return of([]);
      }
      return Jobs.find(query, {
        sort: {
          date: dateSort,
          jobStart: 1,
          'department.name': 1,
        },
        limit: this.limit,
      });
    }),
    shareReplay(1),
  );

  daysList$ = combineLatest([this.allJobs$, this.tabParam$]).pipe(
    map(([jobs, tabParam]) => {
      const daysList: DayListItem[] = [];
      let previousJobDate: string | undefined;

      jobs.forEach((job) => {
        const jobDate = job.date;
        if (previousJobDate === jobDate) {
          daysList[daysList.length - 1].jobIds?.push(job._id);
        } else if (previousJobDate) {
          if (tabParam === JobStatusEnum.ARCHIVED) {
            const tomorrow = format(
              addDays(new Date(jobDate), 1),
              dbDayDateToken,
            );

            daysList.push({
              date: jobDate,
              previousDateRangeStart:
                previousJobDate !== tomorrow
                  ? format(
                      addDays(new Date(previousJobDate), -1),
                      dbDayDateToken,
                    )
                  : undefined,
              previousDateRangeEnd:
                previousJobDate !== tomorrow ? tomorrow : undefined,
              jobIds: [job._id],
            });
          } else {
            const yesterday = format(
              addDays(new Date(jobDate), -1),
              dbDayDateToken,
            );

            daysList.push({
              date: jobDate,
              previousDateRangeStart:
                previousJobDate !== yesterday
                  ? format(
                      addDays(new Date(previousJobDate), 1),
                      dbDayDateToken,
                    )
                  : undefined,
              previousDateRangeEnd:
                previousJobDate !== yesterday ? yesterday : undefined,
              jobIds: [job._id],
            });
          }
        } else {
          // First date encountered
          daysList.push({
            date: jobDate,
            previousDateRangeStart: undefined,
            previousDateRangeEnd: undefined,
            jobIds: [job._id],
          });
        }

        previousJobDate = jobDate;
      });
      return daysList;
    }),
    shareReplay(1),
  );

  jobsLoader$ = combineLatest([
    this.jobsQuery$,
    // view change should show skeletons again
    this.activeView$,
  ]).pipe(
    map(([{ query, dateSort }, activeView]) => ({
      query,
      dateSort,
      // create a new loadedJobIds map when the jobsQuery changes
      loadedJobIds: new Set(),
      activeView,
    })),
    switchMap(({ query, dateSort, loadedJobIds, activeView }) =>
      this.allJobs$.pipe(
        map((jobs) => {
          return { jobs, loadedJobIds };
        }),
        switchMap(({ jobs, loadedJobIds }) =>
          // animationFrames is used to make sure we load items as the browser is ready to load them.
          animationFrames().pipe(
            map(() => ({
              jobsToLoad: jobs.filter((job) => !loadedJobIds.has(job._id)),
            })),
            takeWhile(
              ({ jobsToLoad }) => jobsToLoad.length > 0,
              // inclusive true is needed so that the observable still updates to a result of 0 jobs instead of not emitting it and keeping the last result
              true,
            ),
            map(({ jobsToLoad }) => {
              // load one job
              if (jobsToLoad[0]) {
                loadedJobIds.add(jobsToLoad[0]._id);
              }
              return {
                loadedJobIds,
                activeView,
              };
            }),
          ),
        ),
      ),
    ),
    takeUntil(this.destroyed$),
    shareReplay(1),
  );

  // there is a slight difference between the jobsLoader and the activeView because the view switches virtually instantly.
  // this ensures that the skeletons are shown when switching the view AND not before the view is switched
  jobsLoaderIsForActiveView$ = combineLatest([
    this.jobsLoader$,
    this.activeView$,
  ]).pipe(
    map(([jobsLoader, activeView]) => jobsLoader.activeView === activeView),
  );

  jobsByDateMap$ = this.jobsQuery$.pipe(
    switchMap(() =>
      this.allJobs$.pipe(
        map((jobs) => {
          const jobsByDateMap = new Map<string, Job[]>();
          jobs.forEach((job) => {
            if (jobsByDateMap.get(job.date)) {
              jobsByDateMap.get(job.date)?.push(job);
            } else {
              jobsByDateMap.set(job.date, [job]);
            }
          });
          return jobsByDateMap;
        }),
      ),
    ),
    shareReplay(1),
  );

  i18n = this.translationService.getTranslations('jobs');
  searchText$ = new BehaviorSubject<string>('');
  currentBranch$ = new BehaviorSubject<string | undefined>(undefined);
  currentShift$ = new BehaviorSubject<string | undefined>(undefined);
  currentDemandReason$ = new BehaviorSubject<string | undefined>(undefined);
  shiftEnum = ShiftEnum;
  activeTab$ = new BehaviorSubject<JobStatusEnum>(JobStatusEnum.OPEN);

  private archivedJobsQuery$ = combineLatest([
    this.jobsQueryInputs$,
    this.activeTab$,
  ]).pipe(
    filter(([_, tab]) => tab === JobStatusEnum.ARCHIVED),
    map(([inputs]) =>
      this.buildJobsMongoQueryFromInputs(inputs, JobStatusEnum.ARCHIVED),
    ),
    filter(filterIsNotEmpty),
    rxDistinctJSONStringify(),
    debounceTime(300),
    shareReplay(1),
  );

  @ViewChild('filterStickyStack', { read: ElementRef })
  filterStickyStackRef?: ElementRef<HTMLElement>;

  jobsIndexDayHeaderTopPx: number | null = null;

  jobsIndexDayHeaderTopFallbackPx = 114;
  /** Sticky offset below list column header row (see jobs-list-view). */
  jobsIndexListViewDayHeaderOffsetPx = 48;
  jobsIndexDayHeaderTopExtraPx = 85;
  filterStickyStackResizeObserver?: ResizeObserver;

  readonly dateRangeChips: string[] = [
    'ALL',
    'TOMORROW',
    'TODAY',
    'WEEK',
    'MONTH',
    'CUSTOM',
  ];
  selectedRange$ = new BehaviorSubject<string>('');
  jobsSearchOptions$ = new BehaviorSubject<SearchTypes[]>([
    'ALL',
    'DESCRIPTION',
    'USER',
    'BOOKED_USER',
    'CANCELED_USER',
    'SHIFT',
  ]);
  limit = 100;

  jobStatusEnum = JobStatusEnum;

  tabs$ = new BehaviorSubject<
    { label: string; value: JobStatusEnum; svgIcon: string }[]
  >([
    {
      label: 'open',
      value: JobStatusEnum.OPEN,
      svgIcon: 'inbox',
    },
    {
      label: 'booked',
      value: JobStatusEnum.CLOSED,
      svgIcon: 'board-booked-star',
    },
    {
      label: 'archived',
      value: JobStatusEnum.ARCHIVED,
      svgIcon: 'archive-box',
    },
  ]);
  currentUser$ = new BehaviorSubject<DispoUser | undefined>(undefined);

  certificateParam$: Observable<string | undefined> =
    this.route.queryParams.pipe(
      map((params) => params.certificates ?? undefined),
      // distinctUntilChanged is needed in case of other params changing
      distinctUntilChanged(),
    );

  departmentParam$ = queryParam$(this.route, 'departments');
  selectedDepartmentIds$: Observable<string[]> = this.departmentParam$.pipe(
    map((departments) => {
      if (departments) {
        return departments.split(',').filter((id: string) => id);
      }
      return [];
    }),
    shareReplay(1),
  );

  readyDispoUser$ = this.userService.readyDispo$;
  readyCustomer$ = this.customerService.readyDispoCustomer$;

  disableFilterSave$ = this.readyCustomer$.pipe(
    map((customer) => customer?.disableDispoFilterSave),
    distinctUntilChanged(),
  );

  userEffect = this.readyDispoUser$.pipe(
    takeUntil(this.destroyed$),
    rxNgZoneRun(this.ngZone),
    tap((user) => {
      this.user$.next(user);
      this._loadDefaultFilters(JobStatusEnum.OPEN);
    }),
  );

  columnResizeManager = new ColumnResizeManager(
    'jobListColumnWidths',
    this.ngZone,
  );

  columns$ = combineLatest([
    this.readyCustomer$,
    this.columnResizeManager.columnWidths$,
    this.customerService.isAgency$,
  ]).pipe(
    map(([customer, columnWidths, isAgency]) => {
      let columns: Column[] = [...defaultDispoJobColumns];

      if (customer?.enableInternalDescription) {
        columns.splice(
          columns.findIndex((column) => column.key === 'description'),
          0,
          optionalJobColumns.internalNote,
        );
      }

      if (customer?.enableDemandReasons) {
        columns.splice(
          columns.findIndex((column) => column.key === 'description'),
          0,
          optionalJobColumns.demandReason,
        );
      }

      if (isAgency) {
        const organisationIndex = columns.findIndex(
          (column) => column.key === 'organisation',
        );
        if (organisationIndex !== -1) {
          columns.splice(organisationIndex + 1, 0, {
            key: 'address',
            label: 'Adresse',
            width: '272px',
          });
        }
      }

      // Apply custom widths from localStorage
      const mappedColumns = columns.map((col) => ({
        ...col,
        width: columnWidths[col.key] || col.width,
      }));

      // Ensure CSS rules exist for all column keys with their widths
      this.columnResizeManager.ensureColumnKeysExist(mappedColumns);

      return mappedColumns;
    }),
    shareReplay(1),
  );

  constructor(
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
    private toastService: ToastService,
    private translationService: TranslationService,
    private route: ActivatedRoute,
    private router: Router,
    private subscriptionService: SubscriptionService,
    public permissionsService: PermissionsService,
    private userService: UserService,
    private customerService: CustomerService,
  ) {
    this.userEffect.subscribe();
    this.archivedJobsQuery$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((query) => {
        this.subscriptionService.subscribeArchivedJobs(query);
      });
  }

  private buildJobsMongoQueryFromInputs(
    [
      queryParams,
      user,
      showCanceledJobs,
      onlyShowCanceledJobs,
      showOnlyActionRequiredBookableJobs,
      showOnlyActionRequiredNoOpenInviteesJobs,
      onyShowBookedJobs,
      showUnmappedJobs,
      showOnlyUnapprovedHours,
      selectedSearchType,
      searchTextParam,
    ]: [
      Record<string, string | undefined>,
      DispoUser,
      boolean,
      boolean,
      boolean,
      boolean,
      boolean,
      boolean,
      boolean,
      SearchTypes,
      string | undefined,
    ],
    stateOverride?: JobStatusEnum,
  ) {
    const matchingShifts = getMatchingShiftsForSearchText(
      searchTextParam ?? '',
      this.i18n,
    );
    return getJobsMongoQuery(
      stateOverride ? { ...queryParams, state: stateOverride } : queryParams,
      user,
      {
        showCanceledWithArchived: showCanceledJobs,
        showOnlyCanceledJobs: onlyShowCanceledJobs,
        showOnlyActionRequiredBookableJobs,
        showOnlyActionRequiredNoOpenInviteesJobs,
        showOnlyBookedJobs: onyShowBookedJobs,
        showUnmappedJobs,
        showOnlyUnapprovedHours,
        activeSearchType: selectedSearchType,
        matchingShifts,
      },
    );
  }

  ngOnInit() {
    const jobListPosition = Session.get('jobListPosition');

    if (jobListPosition) {
      setTimeout(() => {
        let listScrollContainer = document.querySelector(
          '.jobs-index-jobs-list.list-view',
        );

        if (listScrollContainer) {
          listScrollContainer.scrollTop = jobListPosition;
        } else {
          window.scrollTo({
            top: jobListPosition,
            left: 0,
            behavior: 'smooth',
          });
        }
      }, 100);
    }
  }

  ngAfterViewInit() {
    this.initJobsIndexDayHeaderTopTracking();
  }

  initJobsIndexDayHeaderTopTracking() {
    const stackEl = this.filterStickyStackRef?.nativeElement;

    if (typeof ResizeObserver === 'undefined') {
      this.updateJobsIndexDayHeaderTopFromFilterHeader();
      return;
    }
    this.filterStickyStackResizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        this.ngZone.run(() =>
          this.updateJobsIndexDayHeaderTopFromFilterHeader(),
        );
      });
    });
    if (stackEl) this.filterStickyStackResizeObserver.observe(stackEl);
    this.updateJobsIndexDayHeaderTopFromFilterHeader();
  }

  updateJobsIndexDayHeaderTopFromFilterHeader() {
    const stackH = this.filterStickyStackRef?.nativeElement?.offsetHeight ?? 0;
    const next = stackH + this.jobsIndexDayHeaderTopExtraPx;
    if (this.jobsIndexDayHeaderTopPx !== next) {
      this.jobsIndexDayHeaderTopPx = next;
      this.cdr.markForCheck();
    }
  }

  /** Grid collapse animates over ~0.2s; refresh after toggle and when it ends. */
  private scheduleStickyStackHeightRefresh(): void {
    const refresh = () =>
      this.ngZone.run(() => this.updateJobsIndexDayHeaderTopFromFilterHeader());
    requestAnimationFrame(refresh);
    setTimeout(refresh, 220);
  }

  onFilterCollapseTransitionEnd(event: TransitionEvent): void {
    if (event.target !== event.currentTarget) return;
    if (event.propertyName !== 'grid-template-rows') return;
    this.ngZone.run(() => this.updateJobsIndexDayHeaderTopFromFilterHeader());
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();

    this.columnResizeManager.cleanup();
    this.filterStickyStackResizeObserver?.disconnect();

    let listScrollContainer = document.querySelector(
      '.jobs-index-jobs-list.list-view',
    );

    Session.set(
      'jobListPosition',
      listScrollContainer ? listScrollContainer.scrollTop : window.scrollY,
    );
  }

  saveDefaultFilter() {
    if (!this.user$.value) return;

    const defaultFilterParams = omitNullJobsDefaultFilterValues(
      getJobsDefaultFilterSaveParams(
        this.activeTab$.value,
        this.route.snapshot.queryParams as Record<
          string,
          string | null | undefined
        >,
      ),
    );

    Meteor.call(
      'saveDispoDefaultFilter',
      this.activeTab$.value,
      defaultFilterParams,
      (error: any) => {
        if (error) {
          console.error('Error saving default filter:', error);
          this.toastService.add({
            type: 'ERROR',
            message: 'Failed to save default filter',
            timeout: 5000,
          });
        } else {
          this.toastService.add({
            type: 'SUCCESS',
            message: 'savedDefaultFilter',
            timeout: 5000,
          });
        }
      },
    );
  }

  enableBranch() {
    let newQueryParam;
    if (this.currentBranch$.value && this.currentBranch$.value != 'ALL') {
      newQueryParam = { branch: this.currentBranch$.value };
    } else {
      newQueryParam = { branch: undefined };
    }
    this.updateQueryParams(newQueryParam);
  }

  enableShift() {
    let newQueryParam;
    if (this.currentShift$.value && this.currentShift$.value != 'ALL') {
      newQueryParam = { shift: this.currentShift$.value };
    } else {
      newQueryParam = { shift: undefined };
    }
    this.updateQueryParams(newQueryParam);
  }

  selectDepartments(departments?: Department[]) {
    const departmentIds = departments
      ? departments.map((department) => department.id)
      : [];
    this.updateQueryParams({
      departments: departmentIds.length > 0 ? departmentIds.join(',') : null,
    });
  }

  enableDemandReason() {
    let newQueryParam;
    if (
      this.currentDemandReason$.value &&
      this.currentDemandReason$.value != 'ALL'
    ) {
      newQueryParam = { demandReason: this.currentDemandReason$.value };
    } else {
      newQueryParam = { demandReason: undefined };
    }
    this.updateQueryParams(newQueryParam);
  }

  enableSearch() {
    let newQueryParam: any = {};
    this.jobsSearchOptions$.value.forEach((type) => {
      newQueryParam[type] = undefined;
    });
    if (this.searchText$.value) {
      newQueryParam[this.selectedSearchType$.value] = this.searchText$.value;
    } else {
      newQueryParam[this.selectedSearchType$.value] = undefined;
    }
    this.updateQueryParams(newQueryParam);
  }

  enableState(state: JobStatusEnum): void {
    this.activeTab$.next(state);

    // @ts-ignore
    this._loadDefaultFilters(state, true);
  }

  trackByJobId(index: number, job: Job) {
    return job._id;
  }

  trackById(index: number, id: string) {
    return id;
  }

  trackByDate(index: number, date: DayListItem) {
    return date.date;
  }

  private updateQueryParams(newQueryParams: any, replace = false) {
    let extras: NavigationExtras = {
      relativeTo: this.route,
      queryParams: newQueryParams,
      replaceUrl: true,
      queryParamsHandling: 'merge',
    };
    if (replace) {
      delete extras.queryParamsHandling;
    }
    this.router.navigate([], extras).then();
  }

  toggleShowCanceledJobs(event: MatCheckboxChange) {
    this.updateQueryParams({ 'show-canceled': event.checked || null });
    this.showCanceledJobs$.next(event.checked);
    this.onyShowBookedJobs$.next(false);

    setTimeout(() => {
      this.toggleOnlyShowBookedJobs({ source: event.source, checked: false });
    });
  }

  toggleOnlyShowCanceledJobs(event: MatCheckboxChange) {
    this.updateQueryParams({ 'only-show-canceled': event.checked || null });
    this.onlyShowCanceledJobs$.next(event.checked);
  }

  toggleShowOnlyActionRequiredBookableJobs(event: MatCheckboxChange) {
    this.updateQueryParams({
      'show-action-required-bookable-only': event.checked || null,
    });
    this.showOnlyActionRequiredBookableJobs$.next(event.checked);
  }

  toggleShowOnlyActionRequiredNoOpenInviteesJobs(event: MatCheckboxChange) {
    this.updateQueryParams({
      'show-action-required-no-open-invitees-only': event.checked || null,
    });
    this.showOnlyActionRequiredNoOpenInviteesJobs$.next(event.checked);
  }

  toggleOnlyShowBookedJobs(event: MatCheckboxChange) {
    this.updateQueryParams({ 'only-show-booked': event.checked || null });
    this.onyShowBookedJobs$.next(event.checked);
  }

  toggleShowUnmappedJobs(event: MatCheckboxChange) {
    this.updateQueryParams({ 'show-unmapped': event.checked || null });
    this.showUnmappedJobs$.next(event.checked);
  }
  toggleShowOnlyUnapprovedHours(event: MatCheckboxChange) {
    this.updateQueryParams({
      'show-only-unapproved-hours': event.checked || null,
    });
    this.showOnlyUnapprovedHours$.next(event.checked);
  }

  private _initFilterInputs(filterValues: any) {
    if (filterValues.branch) {
      this.currentBranch$.next(filterValues.branch);
    } else {
      this.currentBranch$.next('ALL');
    }
    if (filterValues.shift) {
      this.currentShift$.next(filterValues.shift);
    } else {
      this.currentShift$.next('ALL');
    }
    if (filterValues.demandReason) {
      this.currentDemandReason$.next(filterValues.demandReason);
    } else {
      this.currentDemandReason$.next('ALL');
    }
    this.searchText$.next('');
    this.selectedSearchType$.next('ALL');
    this.jobsSearchOptions$.value.forEach((type) => {
      if (filterValues[type]) {
        this.selectedSearchType$.next(type);
        this.searchText$.next(filterValues[type]);
      }
    });

    this.showCanceledJobs$.next(filterValues['show-canceled'] === 'true');
    this.onlyShowCanceledJobs$.next(
      filterValues['only-show-canceled'] === 'true',
    );

    const legacyActionRequired =
      filterValues['show-action-required-only'] === 'true';

    this.showOnlyActionRequiredBookableJobs$.next(
      filterValues['show-action-required-bookable-only'] === 'true' ||
        legacyActionRequired,
    );
    this.showOnlyActionRequiredNoOpenInviteesJobs$.next(
      filterValues['show-action-required-no-open-invitees-only'] === 'true' ||
        legacyActionRequired,
    );

    this.onyShowBookedJobs$.next(filterValues['only-show-booked'] === 'true');

    this.showUnmappedJobs$.next(filterValues['show-unmapped'] === 'true');
    this.showOnlyUnapprovedHours$.next(
      filterValues['show-only-unapproved-hours'] === 'true',
    );

    if (filterValues.range) {
      let splitRange = filterValues.range.split(',');
      this.selectedRange$.next(splitRange[0]);
    } else {
      this.selectedRange$.next('ALL');
    }

    if (filterValues.state) {
      this.activeTab$.next(filterValues.state);
    }
  }

  private _loadDefaultFilters(
    state: JobStatusEnum,
    resetQueryParams?: boolean,
  ) {
    let routeQueryParams = this.route.snapshot.queryParams;
    if (
      routeQueryParams.state &&
      routeQueryParams.state !== routeQueryParams.state.toUpperCase()
    ) {
      const newQueryParams = { state: routeQueryParams.state.toUpperCase() };

      this.router
        .navigate([], {
          relativeTo: this.route,
          queryParams: newQueryParams,
          replaceUrl: true,
          queryParamsHandling: 'merge',
        })
        .then(() => {
          this._loadDefaultFilters(JobStatusEnum.OPEN);
        });
      return;
    }
    let defaultValues;
    let filterValues: Record<string, string | undefined>;
    if (this.user$.value && this.user$.value.defaultFilters && state) {
      if (
        this.user$.value.defaultFilters[state] &&
        Object.keys(this.user$.value.defaultFilters[state])?.length &&
        this.user$.value.defaultFilters[state].state?.toUpperCase() ===
          state.toUpperCase()
      ) {
        defaultValues = this.user$.value.defaultFilters[state];
      }
    }

    if (Object.keys(routeQueryParams).length !== 0 && !resetQueryParams) {
      //query param values
      filterValues = routeQueryParams ||
        defaultValues || {
          range: 'ALL',
          state: state,
        };
    } else if (defaultValues) {
      //user default values
      filterValues = normalizeJobsDefaultFilterForLoad(defaultValues);
    } else if (resetQueryParams && Object.keys(routeQueryParams).length !== 0) {
      // preserve active filters when switching tabs without saved defaults
      filterValues = {
        ...routeQueryParams,
        state: state,
      };
      if (!filterValues.range) {
        filterValues.range = 'ALL';
      }
    } else {
      //origin default values
      filterValues = {
        range: 'ALL',
        state: state,
      };
    }
    this.updateQueryParams(filterValues, true);
    this._initFilterInputs(filterValues);
  }

  toggleFilters(): void {
    this.filtersCollapsed$.next(!this.filtersCollapsed$.value);
    localStorage.setItem(
      'jobIndexFiltersCollapsed',
      this.filtersCollapsed$.value.toString(),
    );
    this.scheduleStickyStackHeightRefresh();
  }

  toggleView() {
    setTimeout(() => {
      const newView = this.activeView$.value === 'tiles' ? 'list' : 'tiles';
      this.activeView$.next(newView);
      localStorage.setItem('jobActiveView', newView);
    }, 50);
  }

  onResizeStart = (event: MouseEvent, columnKey: string) =>
    this.columnResizeManager.onResizeStart(event, columnKey);

  trackByColumnKey = (index: number, column: Column) => column.key;
}
